import {
  Outreach,
  outreachData,
} from "@platform/features/programResources";
import { useCreateOrganizationInvitation } from "@core/features/organization";
import { INVITATION_TYPE, type Invitation } from "@core/features/invitation";
import type { Organization } from "@core/features/organization";
import { Type } from "@themes/pollinator/ui/Type";
import { TrackingCallToActionContainer } from "../TrackingCallToAction";

const PARTNER_AGREEMENT_URL =
  "https://docs.google.com/document/d/1gWdwN3E8TtsxRJm-2L1XXFtpq6Qh7oAF/edit?usp=sharing&ouid=112082936655310092283&rtpof=true&sd=true";

export type Props = {
  /** Organization data */
  organization: Organization;
  /** Participant invitations for coupon codes and links */
  invitations: Invitation[];
  /** Program ID for creating new invitations */
  programId: number | null;
  /** Template ID for creating new invitations */
  templateId: number | null;
};

/**
 * Outreach tab content for organization admin page.
 * Displays marketing materials, copyable content, and downloadable assets
 * for org admins to promote Pollinator to their alumni.
 */
export function OrgOutreachTab({
  organization,
  invitations,
  programId,
  templateId,
}: Props) {
  const { mutate: createInvitation, isPending } =
    useCreateOrganizationInvitation({ organizationId: organization.id });

  const handleCreateInvitation = () => {
    if (!programId || !templateId) return;
    createInvitation({
      programId,
      templateId,
      type: INVITATION_TYPE.PARTICIPANT,
    });
  };

  const invitationData = invitations.map((inv) => ({
    link: `${window.location.origin}/invitation/${inv.shortcode}`,
    code: inv.shortcode,
    name: invitations.length > 1 ? inv.name : undefined,
  }));

  return (
    <div
      className="w-full flex flex-col gap-8 pb-8 max-w-3xl"
      data-testid="org-outreach-tab"
    >
      <TrackingCallToActionContainer />
      <Outreach
        isLoading={false}
        organizationName={organization.name}
        organizationType={organization.type ?? "schools"}
        assetsZipUrl={outreachData.assets.zip.url}
        socialMediaPostsZipUrl={outreachData.assets.socialMediaPostsZip.url}
        socialMediaStoriesZipUrl={outreachData.assets.socialMediaStoriesZip.url}
        invitations={invitationData}
        emailOutreachItem={outreachData.emailOutreachItem}
        newsletterBlurbOutreachItem={outreachData.newsletterBlurbOutreachItem}
        socialMediaOutreachItem={outreachData.socialMediaOutreachItem}
        socialMediaPosts={outreachData.assets.socialMediaPosts}
        socialMediaStories={outreachData.assets.socialMediaStories}
        logos={outreachData.assets.logos}
        onCreateInvitation={handleCreateInvitation}
        isCreatingInvitation={isPending}
        canCreateInvitation={!!programId && !!templateId}
      />
      <Type variant="caption">
        The committment to share Pollinator with your community is outlined in the{" "}
        <a
          href={PARTNER_AGREEMENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-brand-600"
        >
          Partner Agreement
        </a>
        .
      </Type>
    </div>
  );
}

export default OrgOutreachTab;
